<template>
  <div>
    <b-row class="mb-1">
      <b-col class="d-flex justify-content-between align-items-center flex-wrap">
        <div>
          <h1 class="font-weight-bolder mb-0">
            Solicitudes de membresía
          </h1>
          <small class="text-muted">
            Revisa el comprobante y activa el plan del cliente.
          </small>
        </div>
        <b-form-select
          v-model="statusFilter"
          :options="statusOptions"
          style="max-width: 220px"
          @change="load"
        />
      </b-col>
    </b-row>

    <b-overlay :show="loading">
      <b-card>
        <b-table
          :items="requests"
          :fields="fields"
          responsive
          show-empty
          empty-text="No hay solicitudes."
        >
          <template #cell(created_at)="data">
            {{ formatDate(data.item.created_at) }}
          </template>

          <template #cell(amount)="data">
            {{ formatMoney(data.item.amount) }}
            <small class="text-muted d-block">{{ data.item.months }} mes(es)</small>
            <small
              v-if="data.item.discount_percent"
              class="text-success d-block"
            >{{ data.item.discount_percent }}% de descuento sobre {{ formatMoney(data.item.list_amount) }}</small>
          </template>

          <template #cell(receipt_url)="data">
            <b-link
              v-if="data.item.receipt_url"
              :href="data.item.receipt_url"
              target="_blank"
            >
              Ver comprobante
            </b-link>
            <span
              v-else
              class="text-muted"
            >—</span>
          </template>

          <template #cell(status)="data">
            <b-badge :variant="statusVariant(data.item.status)">
              {{ statusLabel(data.item.status) }}
            </b-badge>
            <small
              v-if="data.item.reject_reason"
              class="text-muted d-block"
            >{{ data.item.reject_reason }}</small>
          </template>

          <template #cell(actions)="data">
            <template v-if="data.item.status === 'pending'">
              <b-button
                size="sm"
                variant="success"
                class="mr-50"
                :disabled="loading"
                @click="approve(data.item)"
              >
                Aprobar
              </b-button>
              <b-button
                size="sm"
                variant="outline-danger"
                :disabled="loading"
                @click="reject(data.item)"
              >
                Rechazar
              </b-button>
            </template>
            <span
              v-else
              class="text-muted"
            >{{ data.item.reviewed_by_name }}</span>
          </template>
        </b-table>
      </b-card>
    </b-overlay>
  </div>
</template>

<script>
import {
  BRow, BCol, BCard, BTable, BButton, BBadge, BLink, BOverlay, BFormSelect,
} from 'bootstrap-vue'
import { mapActions, mapGetters } from 'vuex'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BTable,
    BButton,
    BBadge,
    BLink,
    BOverlay,
    BFormSelect,
  },

  data() {
    return {
      statusFilter: 'pending',
      statusOptions: [
        { value: 'pending', text: 'Pendientes' },
        { value: 'approved', text: 'Aprobadas' },
        { value: 'rejected', text: 'Rechazadas' },
        { value: null, text: 'Todas' },
      ],
      fields: [
        { key: 'created_at', label: 'Fecha' },
        { key: 'customer_name', label: 'Cliente' },
        { key: 'membership_plan_name', label: 'Plan' },
        { key: 'amount', label: 'Monto' },
        { key: 'receipt_url', label: 'Comprobante' },
        { key: 'status', label: 'Estado' },
        { key: 'actions', label: '' },
      ],
    }
  },

  computed: {
    ...mapGetters('memberships', {
      requests: 'membershipRequests',
      loading: 'membershipsLoading',
    }),
  },

  created() {
    this.load()
  },

  methods: {
    ...mapActions('memberships', ['getMembershipRequests', 'approveMembershipRequest', 'rejectMembershipRequest']),

    load() {
      return this.getMembershipRequests({
        status: this.statusFilter,
        meta: { pagination: { all: true } },
      })
    },

    formatDate(value) {
      if (!value) return ''
      return new Date(value).toLocaleDateString('es-MX')
    },
    formatMoney(value) {
      return `$${Number(value || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    },

    statusLabel(status) {
      return { pending: 'Pendiente', approved: 'Aprobada', rejected: 'Rechazada' }[status] || status
    },

    statusVariant(status) {
      return { pending: 'light-warning', approved: 'light-success', rejected: 'light-danger' }[status] || 'light-secondary'
    },

    approve(request) {
      return this.approveMembershipRequest(request.id)
        .then(() => {
          this.toast(`Membresía activada para ${request.customer_name}`)
          this.load()
        })
        .catch(error => this.toast(this.errorMessage(error), 'danger'))
    },

    reject(request) {
      const rejectReason = window.prompt('Motivo del rechazo')
      if (!rejectReason) {
        if (rejectReason !== null) this.toast('El motivo es obligatorio', 'warning')
        return null
      }

      return this.rejectMembershipRequest({ requestId: request.id, rejectReason })
        .then(() => {
          this.toast('Solicitud rechazada')
          this.load()
        })
        .catch(error => this.toast(this.errorMessage(error), 'danger'))
    },

    errorMessage(error) {
      return error?.response?.data?.messages?.[0] || 'No se pudo completar la acción'
    },

    toast(title, variant = 'success') {
      this.$toast({
        component: ToastificationContent,
        props: { title, icon: 'AwardIcon', variant },
      })
    },
  },
}
</script>
